import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '../lib/api';
import type { SessionWithDetail } from '@repo/shared-types';
import type { CreateSessionInput, UpdateSessionInput, MarkAttendanceInput, SessionTeacherInput } from '@repo/validation';
import { toast } from 'sonner';
import { invalidateGettingStarted } from './useGettingStarted';

interface RangeOpts { from?: string; to?: string; teacherId?: string; branchId?: string }
function rangeUrl(o?: RangeOpts) {
  const p = new URLSearchParams();
  if (o?.from) p.set('from', o.from);
  if (o?.to) p.set('to', o.to);
  if (o?.teacherId) p.set('teacherId', o.teacherId);
  if (o?.branchId) p.set('branchId', o.branchId);
  const qs = p.toString();
  return qs ? `/sessions?${qs}` : '/sessions';
}
/** `enabled` (default true): every existing caller omits it and keeps
 *  fetching unconditionally — THI-105 added it so useAttentionItems can gate
 *  its own recent-sessions lookup behind the review-nudge window instead of
 *  firing on every workbench visit. */
export function useSessions(opts?: RangeOpts, queryOpts?: { enabled?: boolean }) {
  return useQuery<SessionWithDetail[]>({
    queryKey: ['sessions', { from: opts?.from ?? null, to: opts?.to ?? null, teacherId: opts?.teacherId ?? null, branchId: opts?.branchId ?? null }],
    queryFn: () => fetchApi(rangeUrl(opts)),
    enabled: queryOpts?.enabled ?? true,
  });
}
export function useSession(id: string | null) {
  return useQuery<SessionWithDetail>({
    queryKey: ['sessions', id],
    queryFn: () => fetchApi(`/sessions/${id}`),
    enabled: id !== null,
  });
}
const invalidate = (qc: ReturnType<typeof useQueryClient>) => qc.invalidateQueries({ queryKey: ['sessions'] });

export function useCreateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSessionInput) => fetchApi<SessionWithDetail>('/sessions', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { invalidate(qc); toast.success('Đã thêm buổi học'); },
    onError: (e) => toast.error(e.message || 'Không thể thêm buổi học'),
  });
}
export function useUpdateSession(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateSessionInput) => fetchApi<SessionWithDetail>(`/sessions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => { invalidate(qc); toast.success('Đã cập nhật buổi học'); },
    onError: (e) => toast.error(e.message || 'Không thể cập nhật'),
  });
}
export function useCancelSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetchApi<{ cancelled: boolean }>(`/sessions/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => { invalidate(qc); toast.success('Đã hủy buổi học'); },
    onError: (e) => toast.error(e.message || 'Không thể hủy'),
  });
}
export function useAddParticipant(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enrollmentId: string) => fetchApi(`/sessions/${sessionId}/participants`, { method: 'POST', body: JSON.stringify({ enrollmentId }) }),
    onSuccess: () => { invalidate(qc); qc.invalidateQueries({ queryKey: ['sessions', sessionId] }); },
    onError: (e) => toast.error(e.message || 'Không thể thêm học viên'),
  });
}
export function useRemoveParticipant(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enrollmentId: string) => fetchApi(`/sessions/${sessionId}/participants/${enrollmentId}`, { method: 'DELETE' }),
    onSuccess: () => { invalidate(qc); qc.invalidateQueries({ queryKey: ['sessions', sessionId] }); },
    onError: (e) => toast.error(e.message || 'Không thể bỏ học viên'),
  });
}
export function useMoveParticipant(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { enrollmentId: string; toSessionId: string }) => fetchApi(`/sessions/${sessionId}/participants/move`, { method: 'POST', body: JSON.stringify(vars) }),
    onSuccess: () => { invalidate(qc); toast.success('Đã chuyển suất học'); },
    onError: (e) => toast.error(e.message || 'Không thể chuyển'),
  });
}
export function useReassignSessionTeacher(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teacherId: string) =>
      fetchApi<SessionWithDetail>(`/sessions/${sessionId}`, { method: 'PUT', body: JSON.stringify({ teacherId }) }),
    onSuccess: () => {
      invalidate(qc);
      qc.invalidateQueries({ queryKey: ['sessions', sessionId] });
      toast.success('Đã đổi giáo viên');
    },
    onError: (e) => toast.error(e.message || 'Không thể đổi giáo viên'),
  });
}
/** Save one session's teacher list. Separate from useUpdateSession so the
 *  toast says the right thing and the đổi-GV swap keeps its own. */
export function useUpdateSessionTeachers(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teachers: SessionTeacherInput[]) =>
      fetchApi<SessionWithDetail>(`/sessions/${sessionId}`, { method: 'PUT', body: JSON.stringify({ teachers }) }),
    onSuccess: () => {
      invalidate(qc);
      qc.invalidateQueries({ queryKey: ['sessions', sessionId] });
      toast.success('Đã cập nhật giáo viên của buổi');
    },
    onError: (e) => toast.error(e.message || 'Không thể cập nhật giáo viên'),
  });
}

export function useMarkAttendance(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MarkAttendanceInput) =>
      fetchApi<SessionWithDetail>(`/sessions/${sessionId}/attendance`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sessions'] });
      qc.invalidateQueries({ queryKey: ['sessions', sessionId] });
      qc.invalidateQueries({ queryKey: ['enrollments'] });
      invalidateGettingStarted(qc); // checklist step 5 — the card's last flag
      toast.success('Đã lưu điểm danh');
    },
    onError: (e) => toast.error(e.message || 'Không thể lưu điểm danh'),
  });
}
